import React from 'react';
import {
  FormPublicDesignFragment,
  FormPublicPageFragment,
} from '../../../graphql/fragment/form.public.fragment';
import { StyledButton } from './InnovationTool.css'
import { Box, Grid, LinearProgress, Typography } from '@mui/material';
import ModelOrbBlurred from './assets/blurred_orb.png';
import ModelOrb from './assets/full_orb.png';
import Image from 'next/image';
import {
  START_INNOVATION_MODEL,
  START_LEFT_CONTEXT,
  START_RIGHT_CONTEXT,
} from './utils';

interface Props {
  page: FormPublicPageFragment;
  design: FormPublicDesignFragment;
  className?: string;
  next?: () => void;
  prev?: () => void;
  showProgress?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const StartPage: React.FC<Props> = ({
  page,
  design,
  next,
  prev,
  className,
  showProgress,
}) => {
  if (!page.show) {
    return null;
  }

  return (
    <>
      {className === 'start-page' && (
        <Box
          sx={{
            position: 'absolute',
            top: -330,
            left: 250,
            width: '500px',
            height: '500px',
            zIndex: -2,
            pointerEvents: 'none',
            backgroundImage: `url(${ModelOrbBlurred})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'top center',
            backgroundSize: 'cover',
            margin: 0,
            padding: 0,
            display: { xs: 'none', sm: 'block' },
          }}
        />
      )}

      {className === 'start-page' && (
        <Box
          sx={{
            position: 'absolute',
            top: -415,
            right: -315,
            zIndex: -1,
            width: '800px',
            height: '800px',
            pointerEvents: 'none',
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <Image
            src={ModelOrb}
            alt='orb'
            width={800}
            height={800}
            style={{
              objectFit: 'contain',
              width: '100%',
              height: '100%',
            }}
          />
        </Box>
      )}
      <Grid
        sx={{
          marginTop: { xs: '60px', sm: '113px' },
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: { xs: 'center', sm: 'start' },
          minHeight: { xs: 'calc(100vh - 60px)', sm: 'calc(100vh - 113px)' },
          padding: { xs: '0 8px', sm: '0 24px', md: '0 20vw' },
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            color: '#000',
            fontSize: { xs: '32px', sm: '60px' },
            fontWeight: 300,
            justifyContent: 'start',
            alignItems: { xs: 'center', sm: 'start' },
            textAlign: { xs: 'center', sm: 'start' },
            width: { xs: '100%', sm: '797px' },
          }}
        >
          <Typography
            sx={{
              typography: { xs: 'h5', sm: 'h4' },
              color: design.colors.question,
              paddingBottom: '5px',
            }}
          >
            {'Where should your'}
          </Typography>
          <Typography
            sx={{
              typography: { xs: 'h5', sm: 'h4' },
              color: design.colors.question,
              paddingBottom: '5px',
            }}
          >
            {'organization start to become'}
          </Typography>
          <Typography
            sx={{
              typography: { xs: 'h5', sm: 'h4' },
              fontWeight: 'bold',
              color: design.colors.question,
              paddingBottom: '5px',
            }}
          >
            {'“Innovation Ready”?'}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            fontWeight: 300,
            justifyContent: 'start',
            alignItems: 'start',
            textAlign: { xs: 'center', sm: 'start' },
            gap: { xs: '12px', sm: '25px' },
            width: '100%',
          }}
        >
          <Typography
            sx={{
              color: design.colors.question,
              whiteSpace: 'pre-wrap',
              padding: { xs: '2vh 0', sm: '5vh 0' },
              width: '100%',
              fontSize: { xs: '14px', sm: '16px' },
            }}
          >
            {START_LEFT_CONTEXT}
          </Typography>
          <Typography
            sx={{
              color: design.colors.question,
              whiteSpace: 'pre-wrap',
              padding: { xs: '2vh 0', sm: '5vh 0' },
              width: '100%',
              fontSize: { xs: '14px', sm: '16px' },
            }}
          >
            {START_RIGHT_CONTEXT}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            border: '6px solid #FFF',
            justifyContent: { xs: 'center', sm: 'start' },
            alignItems: { xs: 'center', sm: 'start' },
            padding: { xs: '12px', sm: '25px' },
            marginBottom: { xs: '12px', sm: '20px' },
            marginTop: { xs: '12px' },
            textAlign: { xs: 'center', sm: 'start' },
          }}
        >
          <Typography
            sx={{
              typography: { xs: 'body2', sm: 'body1' },
              color: design.colors.question,
              whiteSpace: 'pre-wrap',
              fontStyle: 'italic',
            }}
          >
            {START_INNOVATION_MODEL}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            fontSize: { xs: '16px', sm: '20px' },
            fontWeight: 300,
            justifyContent: { xs: 'center', sm: 'end' },
            alignItems: { xs: 'center', sm: 'end' },
            textAlign: { xs: 'center', sm: 'end' },
            width: '100%',
          }}
        >
          {className === 'start-page' && (
            <StyledButton
              background={design.colors.button}
              $buttonColor={design.colors.buttonText}
              highlight={design.colors.buttonActive}
              className={'continue-button'}
              width='157px'
              onClick={() => {
                if (showProgress !== undefined && next !== undefined) {
                  showProgress(true);
                  next();
                }
              }}
            >
              {page.buttonText || 'Get Started'}
            </StyledButton>
          )}
        </Box>
      </Grid>
    </>
  );
};
